import { Component, EventEmitter, Input, OnInit, OnChanges, SimpleChanges, Output, ViewChild } from '@angular/core';
import { TreeNode, ConfirmationService, MessageService } from 'primeng/api';
import { eQMSScopeConstants } from '../eqms-scope-constants';
import { NodeChangeEvent, UpdateTreeComponent } from '../../shared/components/update-tree/update-tree.component';
import { ScopeHierarchyUpdateRequest, ScopeHierarchyChange } from '../../shared/models/scope-hierarchy-update-request';
import { EqmsScopeManagementService } from '../../shared/services/eqms-scope-management.service';

@Component({
  selector: 'app-update-scope-hierarchy-slider',
  templateUrl: './update-scope-hierarchy-slider.component.html',
  styleUrls: ['./update-scope-hierarchy-slider.component.scss']
})
export class UpdateScopeHierarchySliderComponent implements OnInit, OnChanges {
  @Output() isHierarchySliderActive = new EventEmitter<boolean>();
  @Output() hierarchyUpdated = new EventEmitter<{ success: boolean; errorMessage?: string }>();
  @Input() isHierarchySliderOpen: boolean = false;
  @Input() treeData: TreeNode[] = [];
  @ViewChild(UpdateTreeComponent) updateTreeComponent!: UpdateTreeComponent;

  hierarchySliderTitle: string = eQMSScopeConstants.eQMSScopeHierarchyUpdateTitle;
  hasChanges: boolean = false;
  isSaving: boolean = false;

  constructor(
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private scopeManagementService: EqmsScopeManagementService
  ) { }

  ngOnInit(): void {
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Reset changes when slider is opened
    if (changes['isHierarchySliderOpen'] && 
        changes['isHierarchySliderOpen'].currentValue === true) {
      this.hasChanges = false;
    }
  }

  closeSlider(): void {
    // Always show confirmation dialog when closing
    this.confirmationService.confirm({
      message: eQMSScopeConstants.Scope_Slider_Confirmation_Text,
      header: eQMSScopeConstants.Scope_Slider_Confirmation_Header,
      icon: 'pi pi-exclamation-triangle',
      acceptIcon: 'pi pi-check',
      rejectIcon: 'pi pi-times',
      accept: () => {
        this.resetSlider();        
      },
      reject: () => {
        // User cancelled - do nothing, stay on slider
      }
    });
  }

  resetSlider() :void {
    // User confirmed - reset and close
        this.hasChanges = false;
        this.isHierarchySliderOpen = false;
        this.isHierarchySliderActive.emit(this.isHierarchySliderOpen);
  }

  onTreeChange(changeEvent: NodeChangeEvent): void {
    // Simply mark that changes exist - don't calculate specific changes yet
    this.hasChanges = changeEvent.treeModified;
  }

  onSave(): void {
    if (this.hasChanges) {
      this.confirmationService.confirm({
        message: 'Modifying the eQMS Scope hierarchy can have major impact on the Training Plans for users assigned to impacted eQMS Scopes. Do you want to proceed?',
        header: 'Confirmation',
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.onConfirmSave();
        },
        reject: () => {
          // User clicked No - do nothing
        }
      });
    }
  }

  private onConfirmSave(): void {
    // Calculate changes only now when user confirms
    const hierarchyChanges = this.updateTreeComponent.calculateHierarchyChanges();
    
    if (hierarchyChanges.length === 0) {
      // No actual changes detected - show info toast immediately without reloading
      this.messageService.add({
        severity: 'info',
        summary: 'No Changes',
        detail: 'No hierarchy changes detected.',
        life: 3000
      });
      return;
    }

    const request: ScopeHierarchyUpdateRequest = {
      hierarchyChanges: hierarchyChanges
    };
    
    console.log('Updating scope hierarchy - Request:', request);
    this.isSaving = true;

    this.scopeManagementService.updateScopeHierarchy(request).subscribe({
      next: (response) => {
        // Reset state
        this.hasChanges = false;
        this.isSaving = false;
        
        // Notify parent to refresh hierarchy with success status
        this.hierarchyUpdated.emit({ success: true });
        
        // Close slider
        this.resetSlider();
      },
      error: (error) => {
        this.isSaving = false;
        
        const errorMessage = error.error?.error || error.message || 'Failed to update scope hierarchy!';
        
        // Notify parent with error status
        this.hierarchyUpdated.emit({ success: false, errorMessage: errorMessage });
        
        // Close slider even on error
        this.resetSlider();
      }
    });
  }
}
